'use client';

import { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Check } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export function VerificationBadge({
  compact = false,
  creator = null,
  mintInfo = null,
  ual,
  oid,
  showText = true,
}) {
  const [expanded, setExpanded] = useState(false);

  // Merge mintInfo with props, do not mutate mintInfo
  const info = {
    ...(mintInfo || {}),
    creator,
    oid,
    ual,
  };

  // Format wallet address for display (0x1234...5678)
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(
      address.length - 4
    )}`;
  };

  // Custom shield icon with check mark
  const ShieldWithCheck = ({ className = 'h-4 w-4' }) => (
    <div className={`relative ${className}`}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="text-green-600 h-full w-full"
      >
        <path
          fillRule="evenodd"
          d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.75.75 0 00.674 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08z"
        />
      </svg>
      <Check className="absolute inset-0 text-white h-full w-full p-1" />
    </div>
  );

  if (compact) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 hover:bg-green-500/20 p-0"
          >
            <ShieldWithCheck />
            <span className="sr-only">Verified by OriginTrail</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 bg-zinc-900 border-white/10 text-white">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-white">Blockchain State</h4>
              <Badge
                variant="outline"
                className="bg-green-500/20 text-green-400 border-green-500/30"
              >
                Verified by{' '}
                <a
                  href="https://origintrail.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  OriginTrail
                </a>
              </Badge>
            </div>

            <div className="text-sm space-y-2">
              <div>
                <span className="text-zinc-400">Chain:</span>{' '}
                <span className="font-medium text-white">
                  {info.chainFullName}
                </span>
              </div>
              <div>
                <span className="text-zinc-400">Token ID:</span>{' '}
                <span className="font-medium text-white">{info.token_id}</span>
              </div>
              <div>
                <span className="text-zinc-400">Creator:</span>{' '}
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-zinc-300">
                    {formatAddress(info.creator)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 py-0 text-zinc-400 hover:text-white hover:bg-white/5"
                  >
                    <span className="text-xs">Copy</span>
                  </Button>
                </div>
              </div>

              {expanded && (
                <>
                  <div>
                    <span className="text-zinc-400">Full Address:</span>{' '}
                    <div className="font-mono text-xs bg-black p-2 rounded border border-white/10 break-all mt-1 text-zinc-300">
                      {info.creator}
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-400">UAL:</span>{' '}
                    <div className="font-mono text-xs bg-black p-2 rounded border border-white/10 break-all mt-1 text-zinc-300">
                      {info.ual}
                    </div>
                  </div>
                  <div>
                    <span className="text-zinc-400">Omniverse ID:</span>{' '}
                    <div className="font-mono text-xs bg-black p-2 rounded border border-white/10 mt-1 text-zinc-300">
                      {info.oid}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-col gap-2 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <>
                    Show less <ChevronUp className="ml-1 h-4 w-4" />
                  </>
                ) : (
                  <>
                    Show more <ChevronDown className="ml-1 h-4 w-4" />
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="w-full flex items-center justify-center bg-zinc-800 border-white/10 text-white hover:bg-zinc-700"
                asChild
              >
                <a
                  href={`https://dkg-testnet.origintrail.io/explore?ual=${encodeURIComponent(
                    info.ual
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full h-full"
                >
                  View on Explorer <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex items-center gap-1 cursor-pointer hover:text-green-400 transition-colors">
          <ShieldWithCheck />
          {showText && (
            <span className="text-xs font-medium text-green-400">
              Verified by{' '}
              <a
                href="https://origintrail.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                OriginTrail
              </a>
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 bg-zinc-900 border-white/10 text-white">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-white">Blockchain Verification</h4>
            <Badge
              variant="outline"
              className="bg-green-500/20 text-green-400 border-green-500/30"
            >
              Verified by{' '}
              <a
                href="https://origintrail.io/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                OriginTrail
              </a>
            </Badge>
          </div>

          <div className="text-sm space-y-2">
            <div>
              <span className="text-zinc-400">Chain:</span>{' '}
              <span className="font-medium text-white">
                {info.chainFullName}
              </span>
            </div>
            <div>
              <span className="text-zinc-400">Token ID:</span>{' '}
              <span className="font-medium text-white">{info.token_id}</span>
            </div>
            <div>
              <span className="text-zinc-400">Creator:</span>{' '}
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-zinc-300">
                  {formatAddress(info.creator)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 py-0 text-zinc-400 hover:text-white hover:bg-white/5"
                >
                  <span className="text-xs">Copy</span>
                </Button>
              </div>
            </div>

            {expanded && (
              <>
                <div>
                  <span className="text-zinc-400">Full Address:</span>{' '}
                  <div className="font-mono text-xs bg-black p-2 rounded border border-white/10 break-all mt-1 text-zinc-300">
                    {info.creator}
                  </div>
                </div>
                <div>
                  <span className="text-zinc-400">UAL:</span>{' '}
                  <div className="font-mono text-xs bg-black p-2 rounded border border-white/10 break-all mt-1 text-zinc-300">
                    {info.ual}
                  </div>
                </div>
                <div>
                  <span className="text-zinc-400">Object ID:</span>{' '}
                  <div className="font-mono text-xs bg-black p-2 rounded border border-white/10 mt-1 text-zinc-300">
                    {info.oid}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  Show less <ChevronUp className="ml-1 h-4 w-4" />
                </>
              ) : (
                <>
                  Show more <ChevronDown className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="w-full flex items-center justify-center bg-zinc-800 border-white/10 text-white hover:bg-zinc-700"
              asChild
            >
              <a
                href={`https://dkg-testnet.origintrail.io/explore?ual=${encodeURIComponent(
                  info.ual
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full h-full"
              >
                View on Explorer <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
